// S13 — SCRAP PARETO, /waste/pareto (Module 05 §4 S13).
//
// THE QUESTION THIS SCREEN EXISTS FOR, and which the product could not answer
// at all before it: *why did we scrap last month, ranked by what it cost, and
// what do I fix first*. Every waste event has carried its cause and its money
// since 05-B; there was no grouping that put the two together and no surface
// that spanned more than one day (S4's category tile is pinned to the picked
// date — see `dayCategories` in shifts.tsx). A GM asking on a Monday got a
// scoreboard for Sunday.
//
// THREE RULES DECIDE EVERYTHING ON THIS SCREEN:
//
//  1. THE RANGE IS FACILITY-LOCAL (D-089). `from`/`to` on `GET /v1/waste/rollups`
//     are BUSINESS DATES, and the server buckets them on the facility's own
//     calendar. So the default — month-to-date — is computed with
//     `facilityTodayIso`, never the browser's clock: a director in Singapore
//     opening the Almaty plant's board must see the plant's month, and for the
//     first hours of every facility day the two calendars disagree.
//
//  2. WHY MONTH-TO-DATE. It is the only default that matches the period the
//     question is asked about. "Last 30 days" straddles two accounting months,
//     so its total can never be reconciled with a P&L line and the number is
//     unusable in the meeting it was opened for. "Last month" is correct on the
//     1st and increasingly stale for the following thirty days. Month-to-date
//     tracks the month being lived in, is non-empty by the first shift of it,
//     and reconciles exactly with the month-end close when the month ends. The
//     two presets cover the other thing a GM actually does — comparing this
//     month with the last one.
//
//  3. WHEN THE MONEY IS REDACTED, THE RANKING CHANGES BASIS AND SAYS SO. §7
//     withholds the ledger's cost from qc / planner / storekeeper / floor /
//     maintenance BY ABSENCE (D-126: the originating module governs its own
//     number, and this ledger's cost is module 05's). The tempting shape —
//     keep the cost ORDER and render the column as `•••` — is dishonest twice:
//     it claims a ranking whose basis it will not show, and it computes that
//     ranking from figures the caller was refused. So the board ranks by
//     QUANTITY instead, one list per UoM (metres and kilograms have no sum,
//     §9.14), and the header states the basis. A planner sees a true Pareto of
//     scrap volume; they do not see a fake Pareto of money.
//
// AND WHAT IT REFUSES TO PRETEND: a cause the catalogue cannot name renders its
// short id, not a blank (`RefLabel`, design-system §3.9); scrap recorded with
// NO cause is its own row with its own words, because it is a finding — the
// unexplained fraction of a month's waste — and dropping it would compute every
// other cause's share against a total the screen never displayed; and a range
// whose total is zero says "no cost is recorded against this scrap yet" rather
// than printing 0.00% down a column, because 0% of nothing is a claim.

import { useMemo, useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { RefLabel } from "../../../../components/ref-label";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { requestIdOf } from "../../../../lib/request";
import { FacilityFilter } from "../../production/components/facility-filter";
import { useFacilities } from "../../production/hooks/production-queries";
import { formatMinor, formatQty, localeLabel } from "../../production/lib/format";
import { isWasteReadRefused, useWastePermissions } from "../hooks/permissions";
import { useWasteCatalog, useWasteRollups, type WasteRollupRow } from "../hooks/waste-queries";
import {
  formatBp,
  paretoOf,
  rowsToReach,
  textOfMilli,
  type ParetoList,
  type ParetoRow,
} from "../lib/pareto";

/** The share a Pareto's "vital few" line reports against (80/20, in bp). */
const VITAL_FEW_BP = 8_000;

export type ParetoDimension = "cause" | "category";

export interface WasteParetoSearch {
  facility_id?: string;
  from?: string;
  to?: string;
  group_by?: ParetoDimension;
}

/** ISO calendar date, loosely — enough to reject a typo before it becomes a
 * request the server 422s. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/u;

export function validateWasteParetoSearch(search: Record<string, unknown>): WasteParetoSearch {
  const out: WasteParetoSearch = {};
  const facility = search["facility_id"];
  if (typeof facility === "string" && facility.length > 0) out.facility_id = facility;
  for (const key of ["from", "to"] as const) {
    const value = search[key];
    if (typeof value === "string" && ISO_DATE.test(value)) out[key] = value;
  }
  const dimension = search["group_by"];
  if (dimension === "cause" || dimension === "category") out.group_by = dimension;
  return out;
}

// --- the range, in the FACILITY's calendar (D-089) --------------------------

export interface DateRange {
  readonly from: string;
  readonly to: string;
}

/** First day of `dateIso`'s month — pure string work, no Date parsing (which
 * would drag the browser's timezone back into a facility-local answer). */
export function monthStartOf(dateIso: string): string {
  return `${dateIso.slice(0, 7)}-01`;
}

/** The month before `dateIso`'s, whole: the "last month" preset and the range a
 * month-end review actually wants. */
export function previousMonthOf(dateIso: string): DateRange {
  const year = Number(dateIso.slice(0, 4));
  const month = Number(dateIso.slice(5, 7));
  const prevYear = month === 1 ? year - 1 : year;
  const prevMonth = month === 1 ? 12 : month - 1;
  const stamp = `${String(prevYear)}-${String(prevMonth).padStart(2, "0")}`;
  // Day 0 of the FOLLOWING month is the last day of this one — the one place a
  // Date is safe, because both ends are UTC-constructed and only the day number
  // is read back.
  const lastDay = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
  return { from: `${stamp}-01`, to: `${stamp}-${String(lastDay).padStart(2, "0")}` };
}

/** Month-to-date in the facility's calendar — the default (see rule 2 above). */
export function monthToDate(nowMs: number, timezone: string | null | undefined): DateRange {
  const today = facilityTodayIso(nowMs, timezone);
  return { from: monthStartOf(today), to: today };
}

/** Is this a range the server can answer? `from > to` is empty by construction,
 * and answering it with an empty state would blame the data for a typo. */
export function rangeIsValid(range: DateRange): boolean {
  return ISO_DATE.test(range.from) && ISO_DATE.test(range.to) && range.from <= range.to;
}

// --- the screen -------------------------------------------------------------

export function WasteParetoScreen() {
  const { t, i18n } = useTranslation();
  const search = useSearch({ from: "/waste/pareto" });

  const perms = useWastePermissions();
  const refused = isWasteReadRefused(perms, "waste.read");

  const facilities = useFacilities();
  const [facilityId, setFacilityId] = useState<string | undefined>(search.facility_id);
  const effectiveFacility = facilityId ?? facilities.data?.items[0]?.id;
  const facilityTimezone = facilities.data?.items.find((f) => f.id === effectiveFacility)?.timezone;

  const [dimension, setDimension] = useState<ParetoDimension>(search.group_by ?? "cause");
  const [picked, setPicked] = useState<DateRange | undefined>(
    search.from !== undefined && search.to !== undefined
      ? { from: search.from, to: search.to }
      : undefined,
  );
  // The default follows the FACILITY once its timezone arrives; an explicit
  // pick is never overwritten.
  const range = picked ?? monthToDate(Date.now(), facilityTimezone);
  const valid = rangeIsValid(range);

  const catalog = useWasteCatalog();
  // A refused read is not issued at all (the S4 lesson: react-query turned one
  // refusal into a dozen requests), and neither is an impossible range.
  const readEnabled = effectiveFacility !== undefined && !refused && valid;
  const rollups = useWasteRollups(
    {
      group_by: dimension,
      from: range.from,
      to: range.to,
      ...(effectiveFacility !== undefined ? { facility_id: effectiveFacility } : {}),
    },
    readEnabled,
  );

  const rows: readonly WasteRollupRow[] = rollups.data ?? [];
  const lists = useMemo(
    () =>
      paretoOf(rows, (row) =>
        dimension === "cause" ? (row.cause_code_id ?? null) : (row.category_id ?? null),
      ),
    [rows, dimension],
  );

  const nameOf = (id: string | null): string | undefined => {
    if (id === null) return undefined;
    const source = dimension === "cause" ? catalog.data?.causes : catalog.data?.categories;
    const row = source?.find((c) => c.id === id);
    return row === undefined ? undefined : localeLabel(row.name_i18n, i18n.language) || row.code;
  };

  const applyPreset = (next: DateRange): void => {
    setPicked(next);
  };
  const today = facilityTodayIso(Date.now(), facilityTimezone);

  return (
    <section className="waste-pareto-screen" data-testid="waste-pareto-screen">
      <PageHeader title={t("waste.pareto.title")} />
      <p className="waste-hint">{t("waste.pareto.intro")}</p>

      <div className="filter-bar" data-testid="waste-pareto-filters">
        <FacilityFilter
          value={effectiveFacility ?? ""}
          facilities={facilities.data?.items ?? []}
          testId="waste-pareto-facility"
          onChange={setFacilityId}
        />
        <Field label={t("waste.pareto.groupBy")}>
          <select
            value={dimension}
            data-testid="waste-pareto-dimension"
            onChange={(e) => {
              setDimension(e.target.value === "category" ? "category" : "cause");
            }}
          >
            <option value="cause">{t("waste.pareto.byCause")}</option>
            <option value="category">{t("waste.pareto.byCategory")}</option>
          </select>
        </Field>
        <Field label={t("waste.pareto.from")}>
          <input
            type="date"
            value={range.from}
            data-testid="waste-pareto-from"
            onChange={(e) => {
              applyPreset({ from: e.target.value, to: range.to });
            }}
          />
        </Field>
        <Field label={t("waste.pareto.to")}>
          <input
            type="date"
            value={range.to}
            data-testid="waste-pareto-to"
            onChange={(e) => {
              applyPreset({ from: range.from, to: e.target.value });
            }}
          />
        </Field>
        <div className="waste-pareto-presets" data-testid="waste-pareto-presets">
          <Button
            variant="secondary"
            data-testid="waste-pareto-preset-mtd"
            onClick={() => {
              applyPreset(monthToDate(Date.now(), facilityTimezone));
            }}
          >
            {t("waste.pareto.presetThisMonth")}
          </Button>
          <Button
            variant="secondary"
            data-testid="waste-pareto-preset-last"
            onClick={() => {
              applyPreset(previousMonthOf(today));
            }}
          >
            {t("waste.pareto.presetLastMonth")}
          </Button>
        </div>
      </div>

      {refused ? (
        <ForbiddenState title={t("waste.access.title")} body={t("waste.access.ledger")} />
      ) : null}

      {/* A BACKWARDS RANGE IS A TYPO, NOT AN EMPTY MONTH. Rendering "no waste in
          this period" for `from > to` would tell a GM their plant scrapped
          nothing, which is the one wrong answer this screen must never give. */}
      {!refused && !valid ? (
        <p className="waste-pareto-range-error" role="alert" data-testid="waste-pareto-range-error">
          {t("waste.pareto.rangeError")}
        </p>
      ) : null}

      {!refused && valid && rollups.fetchStatus === "paused" && rows.length === 0 ? (
        <OfflineState title={t("waste.offline.title")} body={t("waste.offline.body")} />
      ) : null}

      {!refused && valid && rollups.isPending && rollups.fetchStatus !== "paused" && readEnabled ? (
        <TableSkeleton rows={5} label={t("waste.common.loading")} />
      ) : null}

      {!refused && valid && rollups.error !== null ? (
        <ErrorState
          title={t("waste.pareto.error")}
          retryLabel={t("waste.common.retry")}
          requestIdLabel={t("waste.common.requestId")}
          requestId={requestIdOf(rollups.error)}
          onRetry={() => {
            void rollups.refetch();
          }}
        />
      ) : null}

      {!refused &&
      valid &&
      !rollups.isPending &&
      rollups.fetchStatus !== "paused" &&
      rollups.error === null &&
      lists.length === 0 ? (
        <EmptyState title={t("waste.pareto.emptyTitle")} body={t("waste.pareto.emptyBody")} />
      ) : null}

      {lists.map((list) => (
        <ParetoTable
          key={list.basis.kind === "money" ? `c:${list.basis.currency}` : `u:${list.basis.uom}`}
          list={list}
          dimension={dimension}
          nameOf={nameOf}
          namesPending={catalog.isPending}
          facilityId={effectiveFacility}
          range={range}
        />
      ))}
    </section>
  );
}

interface ParetoTableProps {
  list: ParetoList;
  dimension: ParetoDimension;
  nameOf: (id: string | null) => string | undefined;
  namesPending: boolean;
  facilityId: string | undefined;
  range: DateRange;
}

function ParetoTable({
  list,
  dimension,
  nameOf,
  namesPending,
  facilityId,
  range,
}: ParetoTableProps) {
  const { t, i18n } = useTranslation();
  const money = list.basis.kind === "money";
  const vitalFew = rowsToReach(list, VITAL_FEW_BP);

  const valueCell = (row: ParetoRow): string =>
    list.basis.kind === "money"
      ? formatMinor(Number(row.value), list.basis.currency, i18n.language)
      : `${formatQty(textOfMilli(row.value), { locale: i18n.language })} ${list.basis.uom}`;

  const columns: DataTableColumn<ParetoRow>[] = [
    {
      key: "rank",
      header: t("waste.pareto.colRank"),
      numeric: true,
      render: (row) => String(list.rows.indexOf(row) + 1),
    },
    {
      key: "name",
      header: dimension === "cause" ? t("waste.pareto.colCause") : t("waste.pareto.colCategory"),
      render: (row) =>
        // THE UNATTRIBUTED ROW GETS WORDS, NOT AN EM DASH. `RefLabel`'s absent
        // state means "this row has no reference"; here the absence IS the
        // finding — scrap the plant booked without saying why — and it needs a
        // sentence a GM can act on, not a punctuation mark.
        row.key === null ? (
          <span className="waste-pareto-unattributed" data-testid="waste-pareto-unattributed">
            {dimension === "cause" ? t("waste.pareto.noCause") : t("waste.pareto.noCategory")}
          </span>
        ) : (
          <RefLabel value={nameOf(row.key)} id={row.key} pending={namesPending} />
        ),
    },
    {
      key: "value",
      header: money ? t("waste.pareto.colCost") : t("waste.pareto.colQty"),
      numeric: true,
      render: valueCell,
    },
    {
      key: "share",
      header: t("waste.pareto.colShare"),
      numeric: true,
      render: (row) =>
        row.shareBp === null ? t("waste.pareto.shareUnknown") : `${formatBp(row.shareBp)}%`,
    },
    {
      key: "cumulative",
      header: t("waste.pareto.colCumulative"),
      numeric: true,
      render: (row) =>
        row.cumulativeBp === null
          ? t("waste.pareto.shareUnknown")
          : `${formatBp(row.cumulativeBp)}%`,
    },
    {
      key: "events",
      header: t("waste.pareto.colEvents"),
      numeric: true,
      render: (row) => String(row.eventCount),
    },
    {
      key: "qty",
      header: t("waste.pareto.colVolume"),
      render: (row) => (
        <span className="waste-pareto-qty">
          {row.qtyByUom
            .map((line) => `${formatQty(line.qty, { locale: i18n.language })} ${line.uom}`)
            .join(" · ")}
        </span>
      ),
    },
    {
      key: "drill",
      header: t("waste.pareto.colDrill"),
      actions: true,
      render: (row) =>
        // Only a NAMED cause can point the ledger: `cause_code_id` is an
        // equality filter, and there is no "is null" spelling on the wire — so
        // the unattributed row says nothing rather than shipping a link that
        // silently returns the whole month.
        facilityId !== undefined && row.key !== null && dimension === "cause" ? (
          <Link
            to="/waste/events"
            data-testid="waste-pareto-drill"
            search={{
              facility_id: facilityId,
              occurred_from: range.from,
              occurred_to: range.to,
              cause_code_id: row.key,
            }}
          >
            {t("waste.pareto.drill")}
          </Link>
        ) : null,
    },
  ];

  return (
    <Card
      className="waste-pareto-card"
      title={
        money
          ? t("waste.pareto.basisMoney", { currency: list.basis.currency })
          : t("waste.pareto.basisQty", { uom: list.basis.uom })
      }
      data-testid="waste-pareto-list"
    >
      {/* THE BASIS IS STATED, ALWAYS — and when it is quantity, WHY. A reader
          who cannot see why the column changed assumes the screen is broken. */}
      {!money ? (
        <p className="waste-hint" data-testid="waste-pareto-basis-note">
          {t("waste.pareto.redactedNote")}
        </p>
      ) : null}

      {list.total <= 0n ? (
        <p className="waste-hint" data-testid="waste-pareto-zero-total">
          {money ? t("waste.pareto.zeroCost") : t("waste.pareto.zeroQty")}
        </p>
      ) : vitalFew !== null ? (
        <p className="waste-pareto-vital" data-testid="waste-pareto-vital-few">
          {t("waste.pareto.vitalFew", { count: vitalFew })}
        </p>
      ) : null}

      <DataTable
        data-testid="waste-pareto-table"
        columns={columns}
        rows={list.rows}
        rowKey={(row) => row.key ?? "__unattributed__"}
        empty={null}
        caption={t("waste.pareto.tableCaption")}
        rowTestId="waste-pareto-row"
      />
    </Card>
  );
}
